import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Clock, EyeOff, Pencil, Plus, Trash2 } from "lucide-react";

import {
  AdminPage,
  Loading,
  SortableCard,
  SortableList,
  useRows,
  type Row,
} from "@/components/admin/AdminKit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ParticipantsEditor, parseParticipants } from "@/components/admin/ParticipantsEditor";
import type { AgendaParticipant } from "@/lib/types";

export const Route = createFileRoute("/_authenticated/admin/agenda")({
  component: AgendaAdmin,
});

type AgendaForm = {
  id?: string;
  start_time: string;
  end_time: string;
  title: string;
  description: string;
  speakers: string;
  participants: AgendaParticipant[];
  track: string;
  visible: boolean;
  sort_order: number;
};

function AgendaAdmin() {
  const { rows, isLoading, save, remove, reorder } = useRows("agenda_items");
  const [editing, setEditing] = useState<AgendaForm | null>(null);

  if (isLoading) return <Loading />;

  return (
    <AdminPage
      title="Agendă"
      description="Trage punctele pentru a le reordona. Editarea se face în fereastra dedicată."
      action={
        <Button
          onClick={() =>
            setEditing({
              start_time: "",
              end_time: "",
              title: "",
              description: "",
              speakers: "",
              participants: [],
              track: "",
              visible: true,
              sort_order: rows.length,
            })
          }
        >
          <Plus className="mr-2 h-4 w-4" /> Adaugă punct
        </Button>
      }
    >
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Niciun punct în agendă încă.</p>
      ) : (
        <SortableList items={rows} onReorder={reorder}>
          {(item: Row) => (
            <SortableCard key={item.id} id={item.id}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    {Boolean(item["start_time"] || item["end_time"]) && (
                      <span className="flex items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                        <Clock className="h-3 w-3" />
                        {String(item["start_time"] ?? "")}
                        {item["end_time"] ? ` – ${String(item["end_time"])}` : ""}
                      </span>
                    )}
                    {Boolean(item["track"]) && (
                      <span className="rounded-md bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                        {String(item["track"])}
                      </span>
                    )}
                    {!item["visible"] && (
                      <span className="flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                        <EyeOff className="h-3 w-3" /> ascuns
                      </span>
                    )}
                  </div>
                  <p className="mt-1 font-semibold">{String(item["title"] ?? "")}</p>
                  {(() => {
                    const ps = parseParticipants(item["participants"]).filter((p) => p.type !== "group");
                    return ps.length > 0 ? (
                      <p className="text-sm text-muted-foreground">
                        {ps.map((p) => p.name).join(", ")}
                      </p>
                    ) : item["speakers"] ? (
                      <p className="text-sm text-muted-foreground">{String(item["speakers"])}</p>
                    ) : null;
                  })()}
                  {Boolean(item["description"]) && (
                    <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                      {String(item["description"])}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setEditing({
                        id: item.id,
                        start_time: String(item["start_time"] ?? ""),
                        end_time: String(item["end_time"] ?? ""),
                        title: String(item["title"] ?? ""),
                        description: String(item["description"] ?? ""),
                        speakers: String(item["speakers"] ?? ""),
                        participants: parseParticipants(item["participants"]),
                        track: String(item["track"] ?? ""),
                        visible: Boolean(item["visible"]),
                        sort_order: Number(item["sort_order"] ?? 0),
                      })
                    }
                  >
                    <Pencil className="mr-2 h-3.5 w-3.5" /> Editează
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label="Șterge"
                    onClick={() => {
                      if (window.confirm(`Ștergi „${String(item["title"] ?? "")}”?`))
                        void remove(item.id);
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </SortableCard>
          )}
        </SortableList>
      )}

      <Dialog open={editing !== null} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Editează punctul" : "Punct nou în agendă"}</DialogTitle>
            <DialogDescription>Informațiile apar în programul evenimentului.</DialogDescription>
          </DialogHeader>

          {editing && (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-4">
                <div className="space-y-2">
                  <Label>Ora start</Label>
                  <Input
                    value={editing.start_time}
                    onChange={(e) => setEditing({ ...editing, start_time: e.target.value })}
                    placeholder="09:00"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ora final</Label>
                  <Input
                    value={editing.end_time}
                    onChange={(e) => setEditing({ ...editing, end_time: e.target.value })}
                    placeholder="10:30"
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label>Secțiune</Label>
                  <Input
                    value={editing.track}
                    onChange={(e) => setEditing({ ...editing, track: e.target.value })}
                    placeholder="ex. Conferință"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Titlu</Label>
                <Input
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Descriere</Label>
                <Textarea
                  rows={4}
                  value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Moderatori & speakeri (trage pentru ordonare)</Label>
                <ParticipantsEditor
                  value={editing.participants}
                  onChange={(participants) => setEditing({ ...editing, participants })}
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Switch
                  checked={editing.visible}
                  onCheckedChange={(v) => setEditing({ ...editing, visible: v })}
                />
                Vizibil pe site
              </label>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Anulează
            </Button>
            <Button
              onClick={async () => {
                if (!editing) return;
                const ok = await save({ ...editing });
                if (ok) setEditing(null);
              }}
            >
              Salvează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPage>
  );
}
